import React, {PropsWithChildren, useCallback, useContext, useEffect, useState} from "react";
import {InvoiceOption} from "../models/item.model";
import {ItemsContext} from "./ItemsContext";
import {AddOnItemsContext} from "./AddOnItemsContext";
import {WisetackContext} from "./WisetackContext";
import {InvoiceContext} from "./InvoiceContext";
import {CompanyContext} from "./CompanyContext";
import {
    calculateInvoiceSummary, convertFloatToFormattedPrice, convertFormattedPriceToFloat,
    getInvoicePaymentAmount, InvoiceSummary,
} from "../utils/calculate-invoice-summary.util";
import {useCardFeeRecoveryContext} from "./CardFeeRecoveryContext";
import {
    getPreviewInvoiceCardFeeRecoverySummaryRequest,
    getPublicLinkInvoiceCardFeeRecoverySummaryRequest
} from "../api/CardFeeRecoveryApi";
import {PreviewDataContext} from "./PreviewContext";
import {ServerDataContext} from "./ServerDataContext";
import {PageContext} from "./PageContext";


interface OptionContextState {
    isFallbackToPricesWithoutCardFeeRecovery: boolean;
    isCardFeeRecoverySummaryFetching: boolean;
    invoiceSubtotal: string;
    invoiceTax: string;
    invoiceTotal: string;
    invoiceTotalWithoutCardFeeRecovery: string;
    invoiceAmountDue: string;
    invoiceAmountDueWithoutCardFeeRecovery: string;
    invoiceDiscount: string;
    invoiceSurcharge: string;
    selectedOption: InvoiceOption | null;
    selectOption: (option: InvoiceOption) => void;
    clearSelection: () => void;
    selectedAddOnOptions: InvoiceOption[];
    toggleAddOnOption: (option: InvoiceOption) => void;
    getPaymentAmount: () => string;
    getPaymentAmountWithoutCardFeeRecovery: () => string;
    getPaymentAmountFormatted: () => string;
    getPaymentAmountWithoutCardFeeRecoveryFormatted: () => string;
    getAcornLoanAmount: () => number;
}

export const OptionContext = React.createContext({} as OptionContextState);

export const OptionProvider: React.FunctionComponent<PropsWithChildren> = ({children}) => {
    const cardFeeRecovery = useCardFeeRecoveryContext();
    const serverDataContext = useContext(ServerDataContext);
    const options = useContext(ItemsContext).options;
    const addOnOptions = useContext(AddOnItemsContext).addOnItemSection.options;
    const invoice = useContext(InvoiceContext);
    const company = useContext(CompanyContext);
    const wisetackContext = useContext(WisetackContext);
    const previewContext = useContext(PreviewDataContext);
    const pageContext = useContext(PageContext);
    const [selectedOption, setSelectedOption] = useState<InvoiceOption | null>(null);
    const [selectedAddOnOptions, setSelectedAddOnOptions] = useState<InvoiceOption[]>([]);
    const [isCardFeeRecoverySummaryFetching, setIsCardFeeRecoverySummaryFetching] = useState<boolean>(false);
    const [invoiceTotal, setInvoiceTotal] = useState<string>('');
    const [invoiceTotalWithoutCardFeeRecovery, setInvoiceTotalWithoutCardFeeRecovery] = useState<string>('');
    const [invoiceSubtotal, setInvoiceSubtotal] = useState<string>('');
    const [invoiceTax, setInvoiceTax] = useState<string>('');
    const [invoiceDiscount, setInvoiceDiscount] = useState<string>('');
    const [invoiceSurcharge, setInvoiceSurcharge] = useState<string>('');
    const [invoiceAmountDue, setInvoiceAmountDue] = useState<string>('');
    const [invoiceAmountDueWithoutCardFeeRecovery, setInvoiceAmountDueWithoutCardFeeRecovery] = useState<string>('');

    const isFallbackToPricesWithoutCardFeeRecovery = serverDataContext.isFallbackToPricesWithoutCardFeeRecovery;

    // Init selected add-on options based on server data
    useEffect(() => {
        const selectedAddOnOptions: InvoiceOption[] = [];
        if (addOnOptions) {
            for (const option of addOnOptions) {
                if (option.is_selected) {
                    selectedAddOnOptions.push(option);
                }
            }
        }
        setSelectedAddOnOptions(selectedAddOnOptions);
    }, [addOnOptions]);

    const isActivePage = pageContext.isActivePage ? pageContext.isActivePage() : true;
    useEffect(() => {
        if (!options || selectedOption) {
            return;
        }
        const clientSelectedOption = options.find((option: InvoiceOption) => option.is_selected);
        if (clientSelectedOption) {
            setSelectedOption(clientSelectedOption);
            return;
        }
        if (isActivePage) {
            return;
        }
        const isServerSelected = (option: InvoiceOption) => option.is_pdf_selected;
        const serverSelectedOption = options.find(isServerSelected);
        if (!serverSelectedOption) {
            return;
        }
        setSelectedOption(serverSelectedOption);
        setSelectedAddOnOptions((addOnOptions || []).filter(isServerSelected));
    }, [options, addOnOptions, isActivePage, selectedOption]);

    // Init summary
    useEffect(() => {
        if (invoice) {
            if (options?.length === 1) {
                fillSummaryWithInitialDataFromServer();
            } else {
                makeTotalEmpty();
            }
        }
    }, [options, addOnOptions, invoice]);

    // Init and recalculate summary on option, add-ons change
    const selectedOptionIdsStr = selectedOption ? selectedOption.id.toString() : '';
    const selectedAddOnOptionsIdsStr = selectedAddOnOptions.map(option => option.id).join(',');
    useEffect(() => {
        // Card Fee Recovery – fetch summary values from the server to ensure there will be no 1 cent issue
        if (cardFeeRecovery.isEnabled && !isFallbackToPricesWithoutCardFeeRecovery) {
            const abortControllerSignal = recalculateTotalWithCardFeeRecovery();
            return () => {
                abortControllerSignal?.abort();
            };
        }

        // No Card Fee Recovery – recalculate total based on the current selection and existing metadata
        recalculateTotal();
    }, [selectedOptionIdsStr, selectedAddOnOptionsIdsStr, cardFeeRecovery.isEnabled, isFallbackToPricesWithoutCardFeeRecovery]);

    const selectOption = useCallback((option: InvoiceOption) => {
        setSelectedOption(option);
    }, [setSelectedOption]);

    const clearSelection = useCallback(() => {
        setSelectedOption(null);
    }, [setSelectedOption]);

    const fillSummaryWithInitialDataFromServer = () => {
        setInvoiceSubtotal(invoice.subtotal);
        setInvoiceTax(invoice.tax);
        setInvoiceTotal(invoice.total);
        setInvoiceTotalWithoutCardFeeRecovery(invoice.total_without_card_fee_recovery);
        setInvoiceAmountDue(invoice.amount_due);
        setInvoiceAmountDueWithoutCardFeeRecovery(invoice.amount_due_without_card_fee_recovery);
        setInvoiceSurcharge(invoice.surcharge);
        setInvoiceDiscount(invoice.discount);
    }

    const makeTotalEmpty = () => {
        setInvoiceSubtotal(formatPriceWithCompanyCurrencySign(0));
        setInvoiceTax(formatPriceWithCompanyCurrencySign(0));
        setInvoiceTotal(formatPriceWithCompanyCurrencySign(0));
        setInvoiceTotalWithoutCardFeeRecovery(formatPriceWithCompanyCurrencySign(0));
        setInvoiceAmountDue(formatPriceWithCompanyCurrencySign(0));
        setInvoiceAmountDueWithoutCardFeeRecovery(formatPriceWithCompanyCurrencySign(0));
        setInvoiceSurcharge(formatPriceWithCompanyCurrencySign(0));
        setInvoiceDiscount(formatPriceWithCompanyCurrencySign(0));
    }

    const toggleAddOnOption = useCallback((option: InvoiceOption) => {
        if (selectedAddOnOptions.indexOf(option) === -1) {
            setSelectedAddOnOptions([...selectedAddOnOptions, option]);
        } else {
            selectedAddOnOptions.splice(selectedAddOnOptions.indexOf(option), 1);
            setSelectedAddOnOptions([...selectedAddOnOptions]);
        }
    }, [setSelectedAddOnOptions, selectedAddOnOptions]);

    const isSingleOption = () => {
        return options?.length === 1 && !addOnOptions?.length;
    }

    const refreshSummaryBasedOnCurrentSelectionAsync = (selectedOptionId: number, selectedAddOnOptionsIds: number[]): AbortController => {
        const abortController = new AbortController();
        const apiRequest = previewContext.isPreviewMode ? getPreviewInvoiceCardFeeRecoverySummaryRequest : getPublicLinkInvoiceCardFeeRecoverySummaryRequest;

        setIsCardFeeRecoverySummaryFetching(true);
        apiRequest(selectedOptionId, selectedAddOnOptionsIds, abortController.signal)
            .then((response) => {
                setInvoiceSurcharge(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.surcharge)));
                setInvoiceDiscount(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.discount)));
                setInvoiceSubtotal(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.subtotal)));
                setInvoiceTax(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.tax)));
                setInvoiceTotal(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.total)));
                setInvoiceTotalWithoutCardFeeRecovery(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.default.total)));
                setInvoiceAmountDue(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.cfr.amount_unpaid)));
                setInvoiceAmountDueWithoutCardFeeRecovery(formatPriceWithCompanyCurrencySign(convertFormattedPriceToFloat(response.default.amount_unpaid)));

                reloadWisetack(convertFormattedPriceToFloat(response.cfr.total));
            })
            .catch((error) => {
                if (error.name === 'AbortError') {
                    console.info('(Just for information) Fetching CFR Summary was aborted');
                } else {
                    console.log('Error fetching Card Fee Recovery Summary', error);
                }
            })
            .finally(() => {
                setIsCardFeeRecoverySummaryFetching(false);
            });

        return abortController;
    }

    const recalculateTotalWithCardFeeRecovery = (): AbortController | null => {
        // Single option available – automatically fill summary with the init data
        if (isSingleOption()) {
            fillSummaryWithInitialDataFromServer();
            return null;
        }

        // No selected option – clear summary
        if (!selectedOption?.id) {
            makeTotalEmpty();
            return null;
        }

        return refreshSummaryBasedOnCurrentSelectionAsync(selectedOption.id, selectedAddOnOptions.map(option => option.id));
    }

    const recalculateTotal = () => {
        const invoiceSummary: InvoiceSummary | null = calculateInvoiceSummary(
            company,
            invoice,
            isSingleOption(),
            selectedOption,
            selectedAddOnOptions
        );

        if (invoiceSummary) {
            setInvoiceSurcharge(invoiceSummary.surcharge);
            setInvoiceDiscount(invoiceSummary.discount);
            setInvoiceSubtotal(invoiceSummary.subtotal);
            setInvoiceTax(invoiceSummary.tax);
            setInvoiceTotal(invoiceSummary.total);
            setInvoiceTotalWithoutCardFeeRecovery(invoiceSummary.total);
            setInvoiceAmountDue(invoiceSummary.amountRemaining);
            setInvoiceAmountDueWithoutCardFeeRecovery(invoiceSummary.amountRemaining);

            reloadWisetack(convertFormattedPriceToFloat(invoiceSummary.total));
        } else if (!selectedOption && !isSingleOption()) {
            makeTotalEmpty();
        }
    }

    const reloadWisetack = (total: number) => {
        wisetackContext.reloadWisetack(total);
    }

    const formatPriceWithCompanyCurrencySign = (price: number) => {
        return convertFloatToFormattedPrice(price, company.currency_sign);
    };

    const getPaymentAmount = (): string => {
        return getInvoicePaymentAmount(invoice, invoiceTotal, invoiceTotalWithoutCardFeeRecovery, invoiceAmountDue, invoiceAmountDueWithoutCardFeeRecovery, true)
    }

    const getPaymentAmountWithoutCardFeeRecovery = (): string => {
        return getInvoicePaymentAmount(invoice, invoiceTotal, invoiceTotalWithoutCardFeeRecovery, invoiceAmountDue, invoiceAmountDueWithoutCardFeeRecovery, false)
    }

    const getPaymentAmountFormatted = () => {
        return formatPriceWithCompanyCurrencySign(parseFloat(getPaymentAmount()));
    }
    const getPaymentAmountWithoutCardFeeRecoveryFormatted = () => {
        return formatPriceWithCompanyCurrencySign(parseFloat(getPaymentAmountWithoutCardFeeRecovery()));
    }

    // React: OptionContext.tsx (same as PDF Invoice Report)
    const getAcornLoanAmount = (): number => {
        // If there is a payment request, we use it
        if (invoice.is_payment_available) {
            return parseFloat(getPaymentAmount());
        }

        // Otherwise, we use what's allowed: amount due or total amount
        if (invoice.is_status_allows_amount_due) {
            return convertFormattedPriceToFloat(invoiceAmountDue);
        } else {
            return convertFormattedPriceToFloat(invoiceTotal);
        }
    };

    return (
        <OptionContext.Provider
            value={{
                isFallbackToPricesWithoutCardFeeRecovery,
                isCardFeeRecoverySummaryFetching,
                invoiceAmountDue,
                invoiceAmountDueWithoutCardFeeRecovery,
                invoiceSubtotal,
                invoiceTax,
                invoiceTotal,
                invoiceTotalWithoutCardFeeRecovery,
                selectedOption,
                selectedAddOnOptions,
                selectOption,
                clearSelection,
                toggleAddOnOption,
                invoiceDiscount,
                invoiceSurcharge,
                getPaymentAmount,
                getPaymentAmountWithoutCardFeeRecovery,
                getPaymentAmountFormatted,
                getPaymentAmountWithoutCardFeeRecoveryFormatted,
                getAcornLoanAmount,
            }}>
            {children}
        </OptionContext.Provider>
    )
}